import { useCallback, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '~/lib/api-client';
import { chatKeys, chatStatusKeys, questionKeys } from '~/lib/query-keys';
import { AnswerWizard } from '~/features/questions/answer-wizard';
import { fitWizardValue, wizardCounts, type WizardValue } from '~/features/questions/answer-wizard-model';
import type { QuestionDef } from './questions-card';
import { chatAnswerPlaceholder, chatAnswerSendGate, planChatAnswerSend } from './chat-question-dock-model';

export { ChatQuestionsDockedContext } from './chat-questions-docked-context';

interface OpenBatch {
  id: number;
  questions: QuestionDef[];
  status: 'pending' | 'answered' | 'cancelled';
}

export interface ChatQuestionDock {
  /** This surface answers questions in the box: the value for `ChatQuestionsDockedContext`. */
  enabled: boolean;
  /** A batch is open and docked: the host passes the three props below to `ChatInput`. */
  active: boolean;
  topSlot?: (state: { text: string; focusSend: () => void }) => ReactNode;
  sendGate?: (text: string) => { disabled: boolean };
  placeholder?: string;
  /**
   * Call from the composer's submit. Returns null when the text should go out
   * as an ordinary message; otherwise records the answers and resolves to the
   * follow-up message the host must send (the answers, plus the note). A
   * refusal rejects, so the composer keeps what was typed.
   */
  answer: (text: string) => Promise<string> | null;
}

/**
 * FRONT-3223: the chat's open question batch, answered inside the reply box —
 * the Builder's answer panel (FRONT-3155) for chats (eddy, 2026-10-28). Reads
 * the session's current batch (the same lookup the phone uses, refreshed by
 * the SSE `questions` invalidation) and docks only a PENDING one; `enabled`
 * off (the old UI, or a surface without the dock) docks nothing.
 */
type MessageLike = { parts?: ReadonlyArray<{ type: string; toolName?: string; output?: unknown }> };

/**
 * Pure — the id of the newest question batch EDDY asked in the conversation
 * (an `ask_questions` tool output), or null. The dock looks THAT batch up, so
 * a new batch is noticed from the conversation itself: the onboarding page
 * has no live-update stream to say so (FRONT-3223, eddy 2026-10-28: "no
 * questions in the input").
 */
export function latestAskedBatchId(messages: ReadonlyArray<MessageLike>): number | null {
  let id: number | null = null;
  for (const m of messages) {
    for (const p of m.parts ?? []) {
      const isAsk = p.type === 'tool-ask_questions' || (p.type === 'dynamic-tool' && p.toolName === 'ask_questions');
      const batchId = isAsk ? (p.output as { questions?: { id?: unknown } } | undefined)?.questions?.id : undefined;
      if (typeof batchId === 'number') id = batchId;
    }
  }
  return id;
}

export function useChatQuestionDock({
  sessionId,
  enabled,
  turnActive,
  messages = [],
}: {
  sessionId: string;
  enabled: boolean;
  turnActive: boolean;
  /** The conversation, to find the newest batch without a live-update stream. */
  messages?: ReadonlyArray<MessageLike>;
}): ChatQuestionDock {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<{ batchId: number; value: WizardValue } | null>(null);
  const askedId = latestAskedBatchId(messages);
  // The newest asked batch, by the same key and request its card uses (so
  // the two share one answer); with none seen yet, the session's current one.
  const { data } = useQuery({
    queryKey: askedId !== null ? questionKeys.byBatch(sessionId, askedId) : questionKeys.bySession(sessionId),
    queryFn: () =>
      api.get<{ questions: OpenBatch | null }>(
        `/api/questions/current?sessionId=${encodeURIComponent(sessionId)}${askedId !== null ? `&batchId=${askedId}` : ''}`,
      ),
    enabled: enabled && !!sessionId,
    staleTime: 0,
  });
  const batch = enabled && data?.questions?.status === 'pending' ? data.questions : null;
  const value = batch ? fitWizardValue(draft?.batchId === batch.id ? draft.value : undefined, batch.questions.length) : null;

  const answer = useCallback(
    (text: string): Promise<string> | null => {
      if (!batch || !value) return null;
      const plan = planChatAnswerSend({ questions: batch.questions, value, text });
      if (plan.kind === 'plain-message') return null;
      return api
        .post<{ ok: boolean; reason?: string }>('/api/questions/answers', { sessionId, batchId: batch.id, answers: plan.answers })
        .catch((err: unknown) => {
          // Every rejection is paired with a toast: the composer puts the text back on it.
          toast.error((err as Error).message || 'Failed to submit answers');
          throw err;
        })
        .then((res) => {
          if (!res.ok) {
            toast.info(res.reason ?? 'Answers could not be submitted');
            throw new Error('answers-refused');
          }
          setDraft(null);
          qc.invalidateQueries({ queryKey: questionKeys.bySession(sessionId) });
          qc.invalidateQueries({ queryKey: chatKeys.history(sessionId) });
          qc.invalidateQueries({ queryKey: chatStatusKeys.all });
          return plan.message;
        });
    },
    [batch, value, sessionId, qc],
  );

  if (!batch || !value) return { enabled, active: false, answer };
  const counts = wizardCounts(value);
  return {
    enabled,
    active: true,
    placeholder: chatAnswerPlaceholder(counts),
    sendGate: (typed) => chatAnswerSendGate({ counts, typedText: typed, turnActive }),
    topSlot: ({ text, focusSend }) => {
      const gate = chatAnswerSendGate({ counts, typedText: text, turnActive });
      return (
        <div data-testid="chat-question-dock">
          <AnswerWizard
            questions={batch.questions}
            value={value}
            onChange={(next) => setDraft({ batchId: batch.id, value: next })}
            allowSkip
            // The free-text answer is the reply box itself, never a row per question.
            allowOther={false}
            idBase={batch.id}
            onDone={focusSend}
            // No Dismiss row (eddy, 2026-10-28: space): typing past the
            // questions already closes them. Only "still replying" gets a line.
            footer={turnActive && gate.reason ? <span data-testid="chat-question-dock-reason">{gate.reason}</span> : undefined}
          />
        </div>
      );
    },
    answer,
  };
}
